// FLAGS: none
// View model for a space the expert hosts (a native session, private or public) listed in the
// expert's own consults table on My Sessions. Carries no client identity: a private space belongs
// to an employer and a public one is open to every OpenUp user, which is why the Client column
// shows the employer display name, or "OpenUp Users", instead.
import { ResponseConveneHostedSessionResponse } from "@src/api/v1/data-contracts";

export type SpaceStatus = "Scheduled" | "Completed" | "Cancelled";

// The API already resolves this, so it is read rather than re-derived. `ConveneSessionStatus` has
// only Scheduled and Cancelled, but the response mapper widens it at the boundary —
// `HostedStatusToString` returns "cancelled", else "completed"/"scheduled" from
// `EndsAt < DateTime.UtcNow` — so the wire value is already the one to show. Recomputing that
// comparison here would duplicate a rule the backend owns (docs/shared/rules.md
// single-source-of-truth) and let the two drift.
//
// Values arrive lowercase; the Lokalise keys are PascalCase. An unrecognised status reads as
// Scheduled, matching the API's own fallback, so a new member lists rather than blanking the cell.
export const getSpaceStatus = (
  session: Pick<ResponseConveneHostedSessionResponse, "status">,
): SpaceStatus => {
  switch ((session.status ?? "").toLowerCase()) {
    case "cancelled":
      return "Cancelled";
    case "completed":
      return "Completed";
    default:
      return "Scheduled";
  }
};

export type SpaceAudience = "private" | "public";

// `audience` arrives as "private" or "public"; anything else reads as private, the value every row
// had before public group sessions existed (OI-25638), so an unexpected value narrows rather than
// widens what the label claims.
export const getSpaceAudience = (audience?: string | null): SpaceAudience =>
  (audience ?? "").toLowerCase() === "public" ? "public" : "private";

// `eventType` is lowercase on the wire and `ConveneEventType` is a closed set of two. The Type
// column names the audience first and the format second: since OI-25638 the hosted feed returns
// both company-owned sessions and public group sessions, and "Masterclass" alone would not say
// which one the expert is looking at.
//
// An unrecognised event type falls back to the group label rather than throwing, so a third type
// still lists instead of breaking the expert's page. Add the member here when one arrives. The
// Private* keys are registered in Lokalise already; the Public* keys are new and are registered
// alongside this change.
export const SESSION_TYPE_KEYS: Record<
  SpaceAudience,
  Record<string, string>
> = {
  private: {
    groupsession: "MySessions.SessionType.PrivateGroupSession",
    masterclass: "MySessions.SessionType.PrivateMasterclass",
  },
  public: {
    groupsession: "MySessions.SessionType.PublicGroupSession",
    masterclass: "MySessions.SessionType.PublicMasterclass",
  },
};

export const getSessionTypeKey = (
  eventType: string,
  audience: SpaceAudience = "private",
): string =>
  SESSION_TYPE_KEYS[audience][(eventType ?? "").toLowerCase()] ??
  SESSION_TYPE_KEYS[audience].groupsession;

// Hosted spaces only distinguish scheduled / completed / all, while the consult status pills are
// finer (missed by client, cancelled by OpenUp, and so on). `undefined` means this table's status
// has no space equivalent, so the caller lists consults alone rather than dragging in every space
// under the `all` facet, which is what would silently mix cancelled 1:1s with live spaces.
//
// The All Sessions tab is the empty string, not "all": `statusFilters` in SessionFilters gives that
// pill `id: ""` and casts it to ModelsConsultStatus, so "" is a real runtime value the enum does
// not list. Missing that is what kept every space off the All Sessions tab. The API agrees on this
// spelling — ParseHostedStatus treats a zero-length status as All — so "" and "all" are one facet.
export const getSpaceStatusFacet = (status?: string): string | undefined => {
  const facet = (status ?? "").toLowerCase();

  if (facet === "" || facet === "all") return "all";
  if (facet === "scheduled") return "scheduled";
  if (facet === "completed") return "completed";

  return undefined;
};

export interface SpaceRow {
  sessionId: string;
  audience: SpaceAudience;
  /**
   * Shown in the Client column: a private space is owned by an employer, not a client. Empty for
   * a public session, which has no employer; the column shows "OpenUp Users" for it instead.
   */
  employerName: string;
  title: string;
  /** i18n key for the Type column, e.g. "Private Session (Group)". */
  typeLabelKey: string;
  status: SpaceStatus;
  startsAt: string;
  timeZone: string;
  /**
   * Locale code the session is delivered in, e.g. "nl-NL" — the event's own source language, not
   * one it was translated into. Absent for events created before OI-25305, which declared none;
   * the row then shows no flag rather than defaulting to one, since guessing the language an
   * expert is about to speak is worse than saying nothing.
   */
  languageCode?: string;
  /** Whether the expert may request a start link right now; gates the join action. */
  canStart: boolean;
  /**
   * Set on a session the expert hosts that a schedule placed and that is still upcoming: the one
   * kind the expert can hand back ("Withdraw as host"). Hand-added and finished sessions have none.
   */
  withdrawAsHost?: { sessionId: string; title: string };
}

export const mapHostedSessionToSpaceRow = (
  session: ResponseConveneHostedSessionResponse,
): SpaceRow => ({
  sessionId: session.sessionId,
  audience: getSpaceAudience(session.audience),
  employerName: session.employerName,
  title: session.title,
  typeLabelKey: getSessionTypeKey(
    session.eventType,
    getSpaceAudience(session.audience),
  ),
  status: getSpaceStatus(session),
  startsAt: session.startsAt,
  timeZone: session.timeZone,
  // Normalised to undefined so the row has one absent case, not both null and undefined, and the
  // renderer can gate on a single truthiness check.
  languageCode: session.languageCode ?? undefined,
  canStart: session.canStart,
  withdrawAsHost:
    session.isScheduled && session.status.toLowerCase() === "scheduled"
      ? {
          sessionId: session.sessionId,
          title: session.title,
        }
      : undefined,
});
